import {useEffect, useMemo, useState} from 'react';
import {IAllSpecialtyCountyData, IInitialData, ISpecialtyGroupingsData} from '../../types';
import {generateSectionId} from '../../generateTableOfContents';
import PageBreak from '../PageBreak';
import SpecialtyMaps from '../SpecialtyMaps';
import {createPopulationGradientFillPaint, createPassFailFillPaint, POPULATION_GRADIENT, PASS_COLOR, FAIL_COLOR} from '../boundary-layer-strategies';
import MapLegend from '../MapLegend';
import SpecialtyStandards from '../SpecialtyStandards';

const imgHeight = 300;
const imgWidth = 350;
// Limit to 6 concurrent maps to avoid exceeding browser WebGL context limits
// MapBox GL uses WebGL, and browsers typically support 8-16 concurrent contexts
const mapsToRenderParallelly = 6;

interface ICountyProps {
    clientName: string;
    specialtyGroupings: IInitialData<ISpecialtyGroupingsData>;
    allSpecialtyCountyData: IInitialData<IAllSpecialtyCountyData>;
    standardType: string;
    onMapsProgressChange?: (progress: {current: number, total: number}) => void;
}
const County = (props: ICountyProps) => {

    const {clientName, specialtyGroupings, allSpecialtyCountyData: _allSpecialtyCountyData, standardType, onMapsProgressChange} = props;
    const {providerSpecialties = [], facilitySpecialties = []} = specialtyGroupings.data || {};
    const {data: allSpecialtyCountyData, dataErrors: allSpecialtyCountyDataErrors} = _allSpecialtyCountyData.data || {};

    const [mapsLoaded, setMapsLoaded] = useState(0);

    const allSpecialties = useMemo(() => {
        return providerSpecialties.concat(facilitySpecialties);
    }, [providerSpecialties, facilitySpecialties]);

    const totalMaps = allSpecialties.length * 2;

    // Update progress callback whenever maps load
    useEffect(() => {
        onMapsProgressChange?.({current: mapsLoaded, total: totalMaps});
    }, [mapsLoaded, totalMaps, onMapsProgressChange]);

    // Dispatch event when all maps are loaded (82 total: 41 specialties × 2 maps)
    useEffect(() => {
        if (allSpecialties.length && mapsLoaded >= totalMaps) {
            window.dispatchEvent(new CustomEvent('allMapsLoaded'));
        }
    }, [mapsLoaded, allSpecialties, totalMaps]);

    return (
        <article className='county'>
            <h2 id='iii-county-level-results-by-specialty'>III. County Level Results by Specialty</h2>
            <section id='field-definitions'>
                <h3>FIELD DEFINITIONS</h3>
                <p>These fields provide key details used to evaluate the overall network access and adequacy across {clientName}'s primary
                    service area.</p>
                <ul>
                    <li> <b>County designation - </b>County classification used by CMS to assess {standardType}s for compliance</li>
                    <li><b># of Providers and Facilities within T/D - </b>Number of unique provider/facility NPIs that provide care to at least one patient within the time or distance standards</li>
                    <li><b># of Beds Available - </b>Total number of beds in facilities within the time and distance standards</li>
                    <li><b>County Members - </b>Total member count for the given county based on {clientName} membership</li>
                    <li><b>Compliance Target - </b>Percent of members that must have access to a provider or facility within the time and distance standards to be compliant.</li>
                    <li><b>Distance (%) - </b>Percent of members in that county that has access to a provider or facility within actual driving distance (e.g. via GPS) standards. This differs from the {standardType} calculation methodology which relies on Euclidean measures with a multiplier to account for county designations.</li>
                    <li><b>Time (%) - </b>Percent of members that have access to a provider or facility within actual drive time (e.g. via GPS) standards. This differs from the {standardType} calculation methodology which relies on Euclidean measures with a multiplier to account for county designations.</li>
                    <li><b>Status - </b>Counties that are meeting or exceeding the compliance target receive a “Pass” and counties that are not meeting the compliance target receive a “Fail”.</li>
                    <li><b>Additional Providers/Facilities within T/D - </b>Number of additional providers and/or facilities within the time or distance standards that provide access to at least one additional member within that county.</li>
                </ul>
            </section>
            <PageBreak/>
            {allSpecialties.map((specialty, idx) => {
                const {specialty_code, specialty_name} = specialty;
                const tableNumber = 5 + idx;
                const id = generateSectionId(specialty_name);
                const lastCounty = idx === allSpecialties.length - 1;

                const currentFirstImageNumber = idx * 2 + 1;
                const currentSecondImageNumber = idx * 2 + 2;

                const renderFirstImage = (currentFirstImageNumber - mapsLoaded) <= mapsToRenderParallelly;
                const renderSecondImage = (currentSecondImageNumber - mapsLoaded) <= mapsToRenderParallelly;

                const firstMapKey = `map-${specialty_code}-population`;
                const secondMapKey = `map-${specialty_code}-adequacy`;

                const specialtyCountyData = allSpecialtyCountyData ? allSpecialtyCountyData[specialty_code] : null;
                const specialtyCountyDataError = allSpecialtyCountyDataErrors ? allSpecialtyCountyDataErrors[specialty_code] : null;

                // Compute population range for gradient legend
                const populations = (specialtyCountyData || [])
                    .map(r => parseInt(r.county_members, 10))
                    .filter(n => !isNaN(n) && n > 0);
                const minPop = populations.length > 0 ? Math.min(...populations) : 0;
                const maxPop = populations.length > 0 ? Math.max(...populations) : 0;

                return (
                    <section key={specialty_code} id={id}>
                        <h3>{specialty_name}</h3>
                        <div className='maps-section'>
                            <SpecialtyMaps
                                firstMapKey={firstMapKey}
                                secondMapKey={secondMapKey}
                                renderFirstImage={renderFirstImage}
                                renderSecondImage={renderSecondImage}
                                imgWidth={imgWidth}
                                imgHeight={imgHeight}
                                onRenderDone={() => setMapsLoaded(prev => prev + 1)}
                                specialtyCode={specialty_code}
                                countyData={specialtyCountyData}
                                firstMapFillFactory={createPopulationGradientFillPaint}
                                secondMapFillFactory={createPassFailFillPaint}
                                firstMapLegend={
                                    <MapLegend
                                        type="gradient"
                                        title="Member Population"
                                        gradientColors={POPULATION_GRADIENT}
                                        minLabel={minPop.toLocaleString()}
                                        maxLabel={maxPop.toLocaleString()}
                                    />
                                }
                                secondMapLegend={
                                    <MapLegend
                                        type="categorical"
                                        title="Network Adequacy"
                                        items={[
                                            {color: PASS_COLOR, label: 'Pass'},
                                            {color: FAIL_COLOR, label: 'Fail'},
                                        ]}
                                    />
                                }
                            />
                        </div>
                        <h4 className="asset-header">[TABLE {tableNumber}] {specialty_name} ADEQUACY BY COUNTY</h4>
                        <SpecialtyStandards
                            specialtyCountyData={specialtyCountyData}
                            loading={_allSpecialtyCountyData.loading}
                            error={_allSpecialtyCountyData.error || specialtyCountyDataError}
                            providerType={specialty.provider_facility_type}
                        />
                        {!lastCounty && <PageBreak/>}
                    </section>
                );
            })}
        </article>
    );
};

export default County;
